import React, { useEffect, useMemo, useState } from "react";
import { Button, Tabs } from "@life-line-lab/components-react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { omitBy, isNil, isEmpty, isNull } from "lodash";
// antd
import { RightCircleOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { Col, Row } from "antd";
// shared
import { DEFAULT_MAX_PER_PAGE } from "Components/shared/constants";
import ModuleTitleBar from "Components/shared/module-title-bar";
import StatisticBox from "Components/shared/statistic-box";
import { UsersList } from "Components/user-management/user-list";
import { UserProfile } from "Components/user-management/user-profile";
// helper
import { decode, encode, toaster, toNum } from "Helpers/index";
// hooks
import { useAccessControlList, useItemsPerPage, useQuery, useSession } from "Hooks/index";
import usePagination from "Hooks/pagination.hook";

// services
import {
  userActivation,
  fetchSingleUser,
  fetchUserList,
  UserListingFilters,
  fetchUserStatistics,
} from "Services/user.services";
// types
import { AccountRole, Sorting, SortingMode, Statuses } from "Types/common.types";
// scss
import "./user-listing.scss";
import { fetchDiolkoCompaniesList } from "Services/new-driver-management.services";
import { fetchCustomerList } from "Services/customer.service";
import { FetchedUserData, FetchedUserRole } from "Types/user.types";
import { Option } from "Types/common.types";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";
import { useRecoilState, useRecoilValue } from "recoil";
import { userStats } from "Recoil/user.atom";

const { org, profile, roleTypes } = useSession();

const useUsersListing = (props) => {
  const { t } = useTranslation();
  const history = useHistory();

  const { queryParams, filters, itemPerPage, setCurrentListCount, userID, decodedOrgID } =
    props || {};

  const initialSorting = { sortBy: "fullName", mode: SortingMode.DESC };

  const [sorting, setSorting] = useState<Sorting>(initialSorting);
  const [companiesList, setCompaniesList] = useState<Option[]>([]);
  const [customersList, setCustomersList] = useState<Option[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<any>([org._id]);
  const [userStatsCount, setUserStatsCount] = useRecoilState(userStats);
  const [selectedOrgIDs, setSelectedOrgIDs] = useState<string[]>([org._id]);
  const [usersList, setUsersList] = useState<FetchedUserData[]>([]);
  const [userDetails, setUserDetails] = useState<FetchedUserData>({
    userRole: {
      status: "ACTIVE",
    },
  });

  const globalFilterValue = useRecoilValue(GlobalFilterAtom);

  useEffect(() => {
    if (org.orgType === AccountRole.LOGISTIC_PROVIDER) {
      handleFetchCompanies();
    }
    if (org.orgType !== AccountRole.CUSTOMER) {
      handleFetchCustomers();
    }
    let orgIDs: string[] = [];
    orgIDs.push(org._id);

    setSelectedOrgIDs(orgIDs);
  }, []);

  useEffect(() => {
    let orgIDs: string[] = [];
    if (!isEmpty(globalFilterValue.companyIds)) {
      globalFilterValue?.companyIds?.map((orgSelected) => {
        orgIDs.push(orgSelected);
      });
    }
    if (!isEmpty(globalFilterValue.logisticsProviderIds)) {
      globalFilterValue?.logisticsProviderIds?.map((orgSelected) => {
        orgIDs.push(orgSelected);
      });
    }

    !isEmpty(orgIDs) && setSelectedOrgIDs(orgIDs);
  }, [globalFilterValue]);

  useEffect(() => {
    // don't fetch when state is empty or if a specific user is clicked
    if (!isEmpty(selectedOrgIDs)) {
      handleFetchUsersStats();
    }
  }, [selectedOrgIDs]);

  useEffect(() => {
    if (userStatsCount && !isEmpty(selectedOrgIDs)) {
      handleFetchUserList();
    }
  }, [queryParams.offset, filters, itemPerPage, userStatsCount]);

  useEffect(() => {
    if (userID && selectedOrgIDs) {
      handleFetchUserDetails();
    }
  }, [userID]);

  const handleFetchCompanies = async () => {
    const responseData = await fetchDiolkoCompaniesList(org._id);

    if (responseData && !responseData.error) {
      const companiesOption = responseData.orgs.map((data) => {
        return {
          label: data.orgName,
          value: data.orgID,
        };
      });
      setCompaniesList(companiesOption);
    }
  };

  const handleFetchCustomers = async () => {
    const responseData = await fetchCustomerList({}, org._id, Statuses.APPROVED);

    if (responseData && !responseData.error) {
      const customersOption = responseData.map((data) => {
        return {
          label: data.name,
          value: data._id,
        };
      });
      setCustomersList(customersOption);
    }
  };

  const handleFetchUserList = async () => {
    const responseData = await fetchUserList(
      org.orgType,
      org._id,
      selectedOrgIDs ? selectedOrgIDs : org._id,
      org.orgType,
      omitBy(filters, isNil),
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      sorting
    );

    if (responseData && !responseData.error) {
      setUsersList(responseData);
      setCurrentListCount(responseData.length);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleFetchUserDetails = async () => {
    const responseData = await fetchSingleUser(profile._id, org._id, userID, decodedOrgID);

    if (responseData && !responseData.error) {
      setUserDetails(responseData);
    } else {
      toaster("error", responseData?.error?.message);
    }
  };

  // actually isDeactivate is not needed, I dunno why I wrote that initially
  // can remove
  const handleDeactivateUser = async (profileID, isDeactivate, reason) => {
    const action = isDeactivate ? "DEACTIVATED" : "ACTIVE";
    const userID = profileID; // this is id of user (employee), not super admin
    const responseData = await userActivation(profile._id, userID, action, reason);

    if (responseData && !responseData.error) {
      toaster("success", t("toaster.deactivateUserSuccessfully"));
      history.push("/user-management/users");
      handleFetchUserList();
    } else {
      toaster("error", t("toaster.deactivateUserFailed"));
      history.push("/user-management/users");
      handleFetchUserList();
    }
  };

  const handleFetchUsersStats = async () => {
    const responseData = await fetchUserStatistics(selectedOrgIDs);

    if (responseData && !responseData.error) {
      setUserStatsCount({ ...userStatsCount, ...responseData[0] });
    }
  };

  return {
    usersList,
    userDetails,
    userStatsCount,
    companiesList,
    customersList,
    setSelectedOrgIDs,
    handleDeactivateUser,
  };
};

const UsersListing = () => {
  const { id, orgID } = useParams<{ id: string; orgID: string }>();
  const userID = decode(id) || "";
  const decodedOrgID = decode(orgID) || "";

  const query = useQuery();
  const page = query.get("page") || 1;
  const history = useHistory();
  const { t } = useTranslation();

  const userStatistics = [
    { name: "active", tab: "0", iconSrc: "/assets/icons/active-user.svg" },
    { name: "pendingAccount", tab: "1", iconSrc: "/assets/icons/needApproval.svg" },
  ];

  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "User_Management",
  });

  // permissions
  const canList = permission.rights.includes("LIST");
  const canView = permission.rights.includes("VIEW");
  const canCreate = permission.rights.includes("CREATE");
  const canEdit = permission.rights.includes("EDIT");
  const canDelete = permission.rights.includes("DELETE");

  const tabKey = query.get("tabKey") || "0";
  const isActiveUsersTab = tabKey == "0";
  const isPendingUsersTab = tabKey == "1";

  const [count, setCount] = useState(0);
  const [tabActiveKey, setTabActiveKey] = useState(tabKey);
  // ? is forceRender needed
  // const [forceRender, setForceRender] = useState<boolean>(false);

  let initialFilters: UserListingFilters = { status: "ACTIVE" };

  const [filters, setFilters] = useState<UserListingFilters>(initialFilters);

  const [companyFilter, setCompanyFilter] = useState();

  //Pagination

  const [currentListCount, setCurrentListCount] = useState(0);
  const { paginationComponent, itemPerPage, queryParams, setQueryParams } = usePagination({
    currentListCount,
    resetPageDeps: filters
  });

  const userProps = {
    queryParams,
    filters,
    itemPerPage,
    setCurrentListCount,
    userID,
    decodedOrgID,
  };

  const {
    usersList,
    userDetails,
    userStatsCount,
    companiesList,
    customersList,
    setSelectedOrgIDs,
    handleDeactivateUser,
  } = useUsersListing(userProps);

  // modify filters state once tab changes
  useMemo(() => {
    if (isActiveUsersTab) {
      setFilters({ ...filters, status: "ACTIVE" });
    }
    if (isPendingUsersTab) {
      setFilters({ ...filters, status: "PENDING_ACCOUNT" });
    }
  }, [tabActiveKey]);

  // common props for active & pending_account users
  const commonProps = {
    canList,
    canView,
    canEdit,
    canDelete,
    usersList,
    companiesList,
    customersList,
    onSelectOrg(orgID) {
      isEmpty(orgID) ? setSelectedOrgIDs([org._id]) : setSelectedOrgIDs([orgID]);
    },
    onFilter(filterInput) {
      const filter = {
        ...filters,
        orFields: filterInput.orFields,
        roleSubType: filterInput.roles,
      };
      setFilters(filter);
    },
    onViewProfile(orgID, profileID) {
      const encodedOrgID = encode(orgID);
      const id = encode(profileID);
      history.push(`/user-management/user/${encodedOrgID}/${id}/profile`);
    },
    onDeactivateUser(id, isDeactivate, reason) {
      handleDeactivateUser(id, isDeactivate, reason);
    },
  };

  const tabKeyPaginationProps = {
    tabKey: tabKey,
    pathWithPage: {
      withTabKey: `/user-management/users?tabKey=${tabKey}&page=${page}`,
      withoutTabKey: `/user-management/users?page=${page}`,
    },
    pathWithoutPage: {
      withTabKey: `/user-management/users?tabKey=${tabKey}`,
      withoutTabKey: "/user-management/users",
    },
  };

  // const handleItemsPerPageChange = (value: string) => {
  //   setItemPerPage(value)
  // }

  const handlePaginationChange = (value: any) => {
    let page = queryParams.page;
    let limit = itemPerPage;

    if (value === "NEXT") {
      if (usersList.length == itemPerPage) ++page;
      if (page > itemPerPage) page = itemPerPage;
    }

    if (value === "PREVIOUS") {
      --page;
      if (page < 1) page = 1;
    }

    const offset = (page - 1) * limit;
    setQueryParams({ ...queryParams, page, offset });

    if (offset > 0)
      history.push({
        pathname: "/accounts/suppliers",
        search: `?page=${page}`,
      });
    else history.push("/accounts/suppliers");
  };

  // individual tab components
  const tabs = [
    {
      tabKey: "0",
      label: t("tab.activeUsers"),
      component: (
        <UsersList tab={0} {...commonProps} />
      ),
    },
    {
      tabKey: "1",
      label: t("tab.unverifiedUsers"),
      component: (
        <UsersList tab={1} {...commonProps} />
      ),
    },
  ];

  /**
   * function to shift tab to same status as statistic box
   * @param {string} key - tabKey
   */
  const handleStatClick = (key: string) => {
    let path = "";

    if (key === "0") {
      path = page ? `/user-management/users?page=${page}` : `/user-management/users`;
    } else {
      path = page
        ? `/user-management/users?tabKey=${key}&page=${page}`
        : `/user-management/users?tabKey=${key}`;
    }

    setTabActiveKey(key);
    // setForceRender(true);
    history.push(path);
  };

  /**
   * function to change url path when tab changes
   * @param {string} key - tabKey
   */
  const handleTabClick = (key: string) => {
    let path = "";

    if (key === "0") {
      path = page ? `/user-management/users?page=${page}` : `/user-management/users`;
    } else {
      path = page
        ? `/user-management/users?tabKey=${key}&page=${page}`
        : `/user-management/users?tabKey=${key}`;
    }

    setTabActiveKey(key);
    // setForceRender(true);
    history.push(path);
  };

  // function that returns user profile component
  const handleReturnUserView = () => {
    return (
      <UserProfile
        canEdit={canEdit}
        canDelete={canDelete}
        onBack={() => {
          history.goBack();
        }}
        onDeactivateUser={(userID, isDeactivate, reason) =>
          handleDeactivateUser(userID, isDeactivate, reason)
        }
        userDetails={userDetails}
      />
    );
  };

  const handleCreateUserAction = () => {
    history.push("/user-management/user/create");
  };

  return (
    <div className="user-listing">
      <Helmet>
        <title>{t("pageTitle.user")}</title>
      </Helmet>

      <ModuleTitleBar
        titleText={t("title.userManagement")}
        haveSubtitle={id ? true : false}
        subtitleText={id ? t("title.user") : undefined}
      />

      {id ? (
        handleReturnUserView()
      ) : (
        <>
          <Row>
            {canView && (
              <Col flex="auto">
                <div className="user-statistics-group">
                  {userStatistics.map((userStat, index) => {
                    return (
                      <StatisticBox
                        key={index}
                        statObj={userStat}
                        count={userStatsCount[userStat.name]}
                        context="User"
                        onClick={handleStatClick}
                        icon={userStat.iconSrc}
                        iconSrc={userStat.iconSrc}
                      />
                    );
                  })}
                </div>
              </Col>
            )}

            {canCreate && (
              <Col flex="none">
                <div className="action-create-user">
                  <Button
                    label={t("action.createUser")}
                    icon={<RightCircleOutlined />}
                    type="primary"
                    onClick={() => {
                      handleCreateUserAction();
                    }}
                  />
                </div>
              </Col>
            )}
          </Row>

          {/* tabs of different users component */}
          <Tabs
            destroyInactiveTabPane={true}
            onTabClick={handleTabClick}
            defaultActiveKey="0"
            activeKey={tabActiveKey}
            tabBarGutter={25}
            tabs={tabs}
          />
        </>
      )}
      <div className="my-30">{paginationComponent}</div>
    </div>
  );
};

export default UsersListing;
